import type { ReactElement, ReactNode } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { msalInstance } from '@/auth/msalInstance';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { MainLayout } from '@/components/layout/MainLayout';
import { DialogProvider } from '@/components/ui/DialogService';
import { Dashboard } from '@/pages/Dashboard';
import { NotFound } from '@/pages/NotFound';
import { AppSelection } from '@/pages/auth/AppSelection';
import { AuthCallback } from '@/pages/auth/AuthCallback';
import { ErrorNoApps } from '@/pages/auth/ErrorNoApps';
import { ErrorNoUser } from '@/pages/auth/ErrorNoUser';
import { ContainerView } from '@/pages/views/ContainerView';
import { OrderPositionView } from '@/pages/views/OrderPositionView';
import { ReceiptAssignmentView } from '@/pages/views/ReceiptAssignmentView';
import { ReceiptView } from '@/pages/views/ReceiptView';
import { StylePreview } from '@/styles/__preview__/StylePreview';

/**
 * Geschützte Seite im Hauptlayout — Pendant zu AuthorizeRouteView mit DefaultLayout
 * MainLayout aus App.razor: Das Layout umschließt sowohl die Seite als auch den
 * Hinweis für nicht berechtigte Benutzer.
 */
function ProtectedPage({ children }: { children: ReactNode }): ReactElement {
  return (
    <MainLayout>
      <RequireAuth>{children}</RequireAuth>
    </MainLayout>
  );
}

export function App(): ReactElement {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <DialogProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedPage>
                    <Dashboard />
                  </ProtectedPage>
                }
              />
              <Route
                path="/bestellposition/:viewId"
                element={
                  <ProtectedPage>
                    <OrderPositionView />
                  </ProtectedPage>
                }
              />
              <Route
                path="/container/:viewId"
                element={
                  <ProtectedPage>
                    <ContainerView />
                  </ProtectedPage>
                }
              />
              <Route
                path="/rechnung/:viewId"
                element={
                  <ProtectedPage>
                    <ReceiptView />
                  </ProtectedPage>
                }
              />
              <Route
                path="/rechnungszuordnung/:viewId"
                element={
                  <ProtectedPage>
                    <ReceiptAssignmentView />
                  </ProtectedPage>
                }
              />
              <Route
                path="/authentication/apps"
                element={
                  <AuthLayout>
                    <AppSelection />
                  </AuthLayout>
                }
              />
              <Route
                path="/authentication/error/no-apps"
                element={
                  <AuthLayout>
                    <ErrorNoApps />
                  </AuthLayout>
                }
              />
              <Route
                path="/authentication/error/no-user"
                element={
                  <AuthLayout>
                    <ErrorNoUser />
                  </AuthLayout>
                }
              />
              <Route
                path="/authentication/:action"
                element={
                  <AuthLayout>
                    <AuthCallback />
                  </AuthLayout>
                }
              />
              {/*
                Anschauungsseite des Basis-Stylesheets — nur im Entwicklungsmodus erreichbar.
                `import.meta.env.DEV` ist im Produktionsbau statisch `false`, der Zweig und der
                Import entfallen dort vollständig.
              */}
              {import.meta.env.DEV ? (
                <Route path="/__stylepreview" element={<StylePreview />} />
              ) : null}
              <Route
                path="*"
                element={
                  <MainLayout>
                    <NotFound />
                  </MainLayout>
                }
              />
            </Routes>
          </BrowserRouter>
        </DialogProvider>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
